import {downloadTicketPdf, extractFileName, TransportAssocEntity} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {useDebouncedState} from "@mantine/hooks";
import {BaseQueryApi} from "@reduxjs/toolkit/query";

export type UseDownloadTicketPdfProps = {
  baseQueryApi: BaseQueryApi
}

export type UseDownloadTicketPdfResult = {
  downloadTicketPdf: (tta: TransportAssocEntity, onSuccess: (() => void) | undefined) => void
  downloading: boolean
}

export const useDownloadTicketPdf = (props: UseDownloadTicketPdfProps): UseDownloadTicketPdfResult => {
  const {baseQueryApi} = props

  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_downloading, _setDownloading] = useDebouncedState(false, 500)

  useEffect(() => {
    _setDownloading(_downloading)
  }, [_downloading, _setDownloading])


  useEffect(() => {
    if (_downloading) {
      notifications.show({
        id: "useDownloadTicketPdf_updating",
        title: t("common.status.downloading", "Downloading") + " " + t("common.entity.transportasoc", "Ticket"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDownloadTicketPdf_updating")
    }
  }, [_downloading, t]);

  const _handleError = useCallback((rejected: any) => {
    notifications.show({
      color: "red",
      title: t("common.error", "Error"),
      message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
    })
  }, [t])

  const _downloadTicketPdf = useCallback((arg: TransportAssocEntity, onSuccess: (() => void) | undefined) => {
    if (!regionId) {
      notifications.show({
        color: "red",
        title: t("common.error", "Error"),
        message: t<string>("common.error", "Error"),
      })
      return
    }

    _setDownloading(true)

    downloadTicketPdf(arg, baseQueryApi)
      .then((response) => {
        const fileName = extractFileName(response)
        response.headers.forEach((h) => console.debug(h))
        response.blob()
          .then((blob) => {
            _setDownloading(false)

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName ?? `${new Date().getTime()}.pdf`
            a.click();

            notifications.show({
              color: "green",
              title: t("common.entity.transportasoc", "Ticket") + " " + t("common.status.downloaded", "Downloaded"),
              message: ""
            });

            if (onSuccess) {
              onSuccess()
            }
          })
          .catch((rejected) => {
            _handleError(rejected)
          })
          .finally(() => {
            _setDownloading(false)
          })
      })
      .catch((rejected) =>
        _handleError(rejected)
      )
      .finally(() => {
        _setDownloading(false)
      })
  }, [regionId, _setDownloading, baseQueryApi, t, _handleError])

  return {
    downloadTicketPdf: _downloadTicketPdf,
    downloading: _downloading,
  }
}
